import React, { useState, useEffect, useMemo } from 'react';
import { FORUM_TOPICS } from '../data/forumData';
import { ForumTopic, ForumResponse } from '../types';
import { useClient } from '../contexts/ClientContext';
import { useAdmin } from '../contexts/AdminContext';
import { socket, getApiUrl } from '../services/socket';
import { 
    MessageSquareIcon, 
    UserIcon, 
    LockIcon, 
    CalendarIcon, 
    PlusIcon, 
    XIcon, 
    SendIcon, 
    ChevronLeftIcon, 
    ChevronRightIcon, 
    PhoneIcon, SparklesIcon, MessageCircleIcon, ExternalLinkIcon, StarIcon, SearchIcon, TrendingUpIcon, AwardIcon } from './Icons';

const ITEMS_PER_PAGE = 12;

export const getTimestampMs = (item: { createdAt?: string; date?: string }): number => {
    if (item.createdAt) {
        const t = new Date(item.createdAt).getTime();
        if (!isNaN(t)) return t;
    }
    if (!item.date) return Date.now();
    
    const str = (item.date || '').toLowerCase().trim();
    if (str.includes("instant") || str.includes("now")) return Date.now();

    const yearMatch = str.match(/(\d+)\s*(an|ans|year|years)/);
    if (yearMatch) return Date.now() - parseInt(yearMatch[1], 10) * 365 * 86400 * 1000;
    
    const minMatch = str.match(/(\d+)\s*(min|m)/);
    if (minMatch) return Date.now() - parseInt(minMatch[1], 10) * 60 * 1000;
    
    const hourMatch = str.match(/(\d+)\s*(h|heure|hour)/);
    if (hourMatch) return Date.now() - parseInt(hourMatch[1], 10) * 3600 * 1000;
    
    if (str.includes("hier") || str.includes("yesterday")) return Date.now() - 24 * 3600 * 1000;
    const dayMatch = str.match(/(\d+)\s*(j|jour|day)/);
    if (dayMatch) return Date.now() - parseInt(dayMatch[1], 10) * 86400 * 1000;
    
    const weekMatch = str.match(/(\d+)\s*(sem|week)/);
    if (weekMatch) return Date.now() - parseInt(weekMatch[1], 10) * 7 * 86400 * 1000;

    const monthMatch = str.match(/(\d+)\s*(mois|month)/);
    if (monthMatch) return Date.now() - parseInt(monthMatch[1], 10) * 30 * 86400 * 1000;

    const parsed = Date.parse(item.date);
    return isNaN(parsed) ? Date.now() : parsed;
};

export const formatRelativeTime = (createdAtInput?: string | number | Date, fallbackDateStr?: string): string => {
    let timestamp: number;
    if (typeof createdAtInput === 'number') {
        timestamp = createdAtInput;
    } else if (createdAtInput instanceof Date) {
        timestamp = createdAtInput.getTime();
    } else if (typeof createdAtInput === 'string' && createdAtInput) {
        const t = new Date(createdAtInput).getTime();
        timestamp = isNaN(t) ? getTimestampMs({ date: createdAtInput }) : t;
    } else if (fallbackDateStr) {
        timestamp = getTimestampMs({ date: fallbackDateStr });
    } else {
        return "à l'instant";
    }

    const diffMs = Math.max(0, Date.now() - timestamp);
    const mins = Math.floor(diffMs / 60000);

    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `il y a ${days} j`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `il y a ${weeks} sem.`;
    const months = Math.floor(days / 30);
    if (months < 12) return `il y a ${Math.max(1, months)} mois`;
    const years = Math.floor(months / 12);
    return `il y a ${years} an${years > 1 ? 's' : ''}`;
};

const getCategoryFlairStyle = (category: string) => {
    switch (category) {
        case 'Amour & Couple':
            return 'bg-pink-950/80 text-pink-300 border-pink-500/40 hover:bg-pink-900/90';
        case 'Voyance Pure':
            return 'bg-purple-950/80 text-purple-300 border-purple-500/40 hover:bg-purple-900/90';
        case 'Tarot':
            return 'bg-amber-950/80 text-amber-300 border-amber-500/40 hover:bg-amber-900/90';
        case 'Astrologie':
            return 'bg-blue-950/80 text-blue-300 border-blue-500/40 hover:bg-blue-900/90';
        case 'Interprétation Rêves':
            return 'bg-indigo-950/80 text-indigo-300 border-indigo-500/40 hover:bg-indigo-900/90';
        case 'Témoignages':
            return 'bg-emerald-950/80 text-emerald-300 border-emerald-500/40 hover:bg-emerald-900/90';
        default:
            return 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700';
    }
};

const Forum: React.FC = () => {
    const { client, openAuthModal } = useClient();
    const { updateAiMemory } = useAdmin();
    const [selectedCategory, setSelectedCategory] = useState('Tout');
    const [sortBy, setSortBy] = useState<'hot' | 'new' | 'top'>('new');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTopic, setSelectedTopic] = useState<ForumTopic | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [topics, setTopics] = useState<ForumTopic[]>([]);
    const [userVotes, setUserVotes] = useState<Record<string, 'up' | 'down'>>({});
    const [savedPosts, setSavedPosts] = useState<Record<string, boolean>>({});
    const [copiedPostId, setCopiedPostId] = useState<string | null>(null);
    const [isJoinedCommunity, setIsJoinedCommunity] = useState(true);
    const [onlineCount, setOnlineCount] = useState<number>(() => {
        const hour = new Date().getHours();
        let base = 380;
        if (hour >= 13 && hour <= 23) {
            base = 520 + Math.floor(Math.random() * 190);
        } else if (hour >= 1 && hour <= 7) {
            base = 180 + Math.floor(Math.random() * 110);
        } else {
            base = 340 + Math.floor(Math.random() * 180);
        }
        return base;
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setOnlineCount(prev => {
                const hour = new Date().getHours();
                let minLimit = 150;
                let maxLimit = 750;
                if (hour >= 13 && hour <= 23) {
                    minLimit = 420;
                    maxLimit = 760;
                } else if (hour >= 1 && hour <= 7) {
                    minLimit = 150;
                    maxLimit = 320;
                } else {
                    minLimit = 280;
                    maxLimit = 590;
                }
                const delta = Math.floor(Math.random() * 11) - 5;
                return Math.max(minLimit, Math.min(maxLimit, prev + delta));
            });
        }, 4000);
        return () => clearInterval(interval);
    }, []);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);

    const [replyText, setReplyText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showNewTopicForm, setShowNewTopicForm] = useState(false);
    const [newTopicTitle, setNewTopicTitle] = useState('');
    const [newTopicCategory, setNewTopicCategory] = useState('Amour & Couple');
    const [newTopicContent, setNewTopicContent] = useState('');

    const [isEditingTopic, setIsEditingTopic] = useState(false);
    const [editTopicTitle, setEditTopicTitle] = useState('');
    const [editTopicCategory, setEditTopicCategory] = useState('');
    const [editTopicContent, setEditTopicContent] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [editingResponse, setEditingResponse] = useState<ForumResponse | null>(null);
    const [replyingToComment, setReplyingToComment] = useState<{ id: string; author: string } | null>(null);
    const [editResponseText, setEditResponseText] = useState('');

    useEffect(() => {
        const STORAGE_KEY = 'astro_forum_data_v3';
        const VOTES_KEY = 'astro_forum_votes_v1';
        const SAVED_KEY = 'astro_forum_saved_v1';

        const savedVotes = localStorage.getItem(VOTES_KEY);
        if (savedVotes) {
            try { setUserVotes(JSON.parse(savedVotes)); } catch (e) {}
        }

        const savedSaved = localStorage.getItem(SAVED_KEY);
        if (savedSaved) {
            try { setSavedPosts(JSON.parse(savedSaved)); } catch (e) {}
        }

        // 1. Initialiser d'abord depuis localStorage pour affichage instantané
        const savedTopics = localStorage.getItem(STORAGE_KEY);
        if (savedTopics) {
            try {
                const parsed = JSON.parse(savedTopics);
                if (Array.isArray(parsed)) {
                    setTopics(parsed);
                }
            } catch (e) {
                setTopics([]);
            }
        } else {
            setTopics([]);
        }

        // 2. Récupérer les sujets frais depuis le serveur VPS (/api/forum/topics)
        fetch(`${getApiUrl()}/api/forum/topics`)
            .then(res => res.json())
            .then(serverTopics => {
                if (Array.isArray(serverTopics)) {
                    setTopics(serverTopics);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(serverTopics));
                }
            })
            .catch(err => console.log('[FORUM] Mode local/hors-ligne utilisé:', err));

        // 3. Écoute des mises à jour en direct via WebSocket (génération Ollama, nouveaux posts, réponses, purge)
        const handleForumUpdated = (updatedTopics: ForumTopic[]) => {
            if (Array.isArray(updatedTopics)) {
                setTopics(updatedTopics);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTopics));
                // Mettre à jour le topic sélectionné si ouvert
                setSelectedTopic(prev => {
                    if (!prev) return null;
                    const found = updatedTopics.find(t => t.id === prev.id);
                    return found || null;
                });
            }
        };

        socket.on('forum_updated', handleForumUpdated);

        return () => {
            socket.off('forum_updated', handleForumUpdated);
        };
    }, []);

    // Restauration automatique du sujet ouvert après actualisation (F5) ou via le Hash URL
    useEffect(() => {
        if (topics.length === 0) return;
        const hash = (typeof window !== 'undefined' ? window.location.hash : '').toLowerCase();
        let targetTopicId: string | null = null;

        if (hash.includes('forum-')) {
            targetTopicId = hash.split('forum-')[1];
        } else if (hash.includes('forum/')) {
            targetTopicId = hash.split('forum/')[1];
        } else {
            try {
                targetTopicId = sessionStorage.getItem('last_forum_topic_id');
            } catch (e) {}
        }

        if (targetTopicId) {
            const found = topics.find(t => t.id.toLowerCase() === targetTopicId?.toLowerCase());
            if (found && (!selectedTopic || selectedTopic.id !== found.id)) {
                setSelectedTopic(found);
            }
        }
    }, [topics]);

    // Écoute des navigations navigateur (Bouton Précédent / Suivant)
    useEffect(() => {
        const handleHashChange = () => {
            const hash = (typeof window !== 'undefined' ? window.location.hash : '').toLowerCase();
            if (hash === '#forum' || hash === '#/forum' || hash === '') {
                setSelectedTopic(null);
                try { sessionStorage.removeItem('last_forum_topic_id'); } catch (e) {}
            } else if (hash.includes('forum-')) {
                const targetTopicId = hash.split('forum-')[1];
                const found = topics.find(t => t.id.toLowerCase() === targetTopicId.toLowerCase());
                if (found) {
                    setSelectedTopic(found);
                    try { sessionStorage.setItem('last_forum_topic_id', found.id); } catch (e) {}
                }
            }
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [topics]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage, selectedTopic]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, searchTerm, sortBy]);

    const categories = ['Tout', 'Amour & Couple', 'Voyance Pure', 'Tarot', 'Astrologie', 'Interprétation Rêves', 'Témoignages'];
    const creationCategories = categories.filter(c => c !== 'Tout');

    const getTopicBaseVotes = (topic: ForumTopic): number => {
        const idHash = topic.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return Math.max(3, (idHash % 45) + (topic.replies * 2) + Math.floor((topic.views || 10) / 15));
    };

    const getTopicCurrentVotes = (topic: ForumTopic): number => {
        const base = getTopicBaseVotes(topic);
        const userVote = userVotes[topic.id];
        if (userVote === 'up') return base + 1;
        if (userVote === 'down') return Math.max(0, base - 1);
        return base;
    };

    const handleVote = (topicId: string, type: 'up' | 'down', e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!client) {
            openAuthModal();
            return;
        }

        const current = userVotes[topicId];
        let nextVote: 'up' | 'down' | null = null;

        if (current === type) {
            nextVote = null;
        } else {
            nextVote = type;
        }

        const updated = { ...userVotes };
        if (nextVote) {
            updated[topicId] = nextVote;
        } else {
            delete updated[topicId];
        }

        setUserVotes(updated);
        localStorage.setItem('astro_forum_votes_v1', JSON.stringify(updated));
    };

    const handleToggleSave = (topicId: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const updated = { ...savedPosts, [topicId]: !savedPosts[topicId] };
        setSavedPosts(updated);
        localStorage.setItem('astro_forum_saved_v1', JSON.stringify(updated));
    };

    const handleSharePost = (topic: ForumTopic, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (navigator.clipboard) {
            navigator.clipboard.writeText(`${window.location.origin}/#forum-${topic.id}`);
            setCopiedPostId(topic.id);
            setTimeout(() => setCopiedPostId(null), 2500);
        }
    };

    const filteredTopics = useMemo(() => {
        const list = topics.filter(topic => {
            const matchCat = selectedCategory === 'Tout' || topic.category === selectedCategory;
            const matchSearch = (topic.title || '').toLowerCase().includes((searchTerm || '').toLowerCase()) || 
                                (topic.content || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
                                (topic.author || '').toLowerCase().includes((searchTerm || '').toLowerCase());
            return matchCat && matchSearch;
        });

        return [...list].sort((a, b) => {
            if (sortBy === 'hot') {
                const scoreA = getTopicCurrentVotes(a) * 3 + a.replies * 5 - (Date.now() - getTimestampMs(a)) / (1000 * 3600 * 24);
                const scoreB = getTopicCurrentVotes(b) * 3 + b.replies * 5 - (Date.now() - getTimestampMs(b)) / (1000 * 3600 * 24);
                return scoreB - scoreA;
            } else if (sortBy === 'new') {
                return getTimestampMs(b) - getTimestampMs(a);
            } else if (sortBy === 'top') {
                return getTopicCurrentVotes(b) - getTopicCurrentVotes(a);
            }
            return 0;
        });
    }, [topics, selectedCategory, searchTerm, sortBy, userVotes]);

    const sortedResponses = useMemo(() => {
        if (!selectedTopic?.responses) return [];
        return [...selectedTopic.responses].sort((a, b) => getTimestampMs(a) - getTimestampMs(b));
    }, [selectedTopic]);

    const totalPages = Math.ceil(filteredTopics.length / ITEMS_PER_PAGE);
    const currentTopics = filteredTopics.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handleTopicClick = (topic: ForumTopic) => {
        const updated = topics.map(t => t.id === topic.id ? { ...t, views: (t.views || 0) + 1 } : t);
        setTopics(updated);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updated));
        setSelectedTopic({ ...topic, views: (topic.views || 0) + 1 });
        if (typeof window !== 'undefined') {
            window.location.hash = `#forum-${topic.id}`;
            try { sessionStorage.setItem('last_forum_topic_id', topic.id); } catch(e) {}
        }
    };

    const handleBack = () => {
        setSelectedTopic(null);
        setReplyText('');
        setReplyingToComment(null);
        if (typeof window !== 'undefined') {
            window.location.hash = '#forum';
            try { sessionStorage.removeItem('last_forum_topic_id'); } catch(e) {}
        }
    };

    const handleCreateTopicClick = () => {
        if (!client) openAuthModal();
        else setShowNewTopicForm(true);
    };

    const handleSubmitTopic = (e: React.FormEvent) => {
        e.preventDefault();
        if (!client || !newTopicTitle.trim() || !newTopicContent.trim()) return;

        setIsSubmitting(true);

        const now = new Date();
        const newTopic: ForumTopic = {
            id: `user-topic-${now.getTime()}`,
            title: newTopicTitle,
            category: newTopicCategory,
            content: newTopicContent,
            author: client.name,
            createdAt: now.toISOString(),
            date: "À l'instant",
            replies: 0,
            views: 1,
            responses: []
        };

        const updatedTopics = [newTopic, ...topics];
        setTopics(updatedTopics);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updatedTopics));
        
        // Notification serveur et diffusion temps réel
        socket.emit('forum_create_topic', newTopic);
        fetch(`${getApiUrl()}/api/forum/topics`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newTopic)
        }).catch(err => console.log('[FORUM] API post fallback:', err));

        updateAiMemory(client.phone, 0, client.name, [{ category: 'psychologie', fact: `A ouvert un sujet Forum "${newTopicCategory}" : ${newTopicTitle}`, date: now.toISOString() }], [], "Actif / En recherche de réponse", false);

        setNewTopicTitle('');
        setNewTopicContent('');
        setNewTopicCategory('Amour & Couple');
        setIsSubmitting(false);
        setShowNewTopicForm(false);
        setSelectedTopic(newTopic);
    };

    const handleSubmitReply = (e: React.FormEvent) => {
        e.preventDefault();
        if (!replyText.trim() || !client || !selectedTopic) return;

        setIsSubmitting(true);

        const now = new Date();
        const newReply: ForumResponse = {
            id: `user_resp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            author: client.name,
            text: replyText.trim(),
            createdAt: now.toISOString(),
            date: "À l'instant",
            parentId: replyingToComment ? replyingToComment.id : undefined,
            replyToAuthor: replyingToComment ? replyingToComment.author : undefined,
            isUser: true 
        };

        const updatedTopics = topics.map(t => {
            if (t.id === selectedTopic.id) {
                return { ...t, responses: [...t.responses, newReply], replies: t.replies + 1 };
            }
            return t;
        });

        setTopics(updatedTopics);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updatedTopics));
        
        const updatedCurrentTopic = updatedTopics.find(t => t.id === selectedTopic.id);
        if (updatedCurrentTopic) setSelectedTopic(updatedCurrentTopic);

        // Notification serveur et diffusion temps réel
        socket.emit('forum_reply_topic', { topicId: selectedTopic.id, reply: newReply });
        fetch(`${getApiUrl()}/api/forum/topics/${selectedTopic.id}/reply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newReply)
        }).catch(err => console.log('[FORUM] API reply fallback:', err));

        updateAiMemory(client.phone, 0, client.name, [{ category: 'psychologie', fact: `A commenté sur le sujet "${selectedTopic.title}" (${selectedTopic.category})`, date: now.toISOString() }], [], "Participatif / Engagé", false);
        setReplyingToComment(null);

        setReplyText('');
        setIsSubmitting(false);
    };

    const handleDeleteTopic = (topicId: string) => {
        const updatedTopics = topics.filter(t => t.id !== topicId);
        setTopics(updatedTopics);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updatedTopics));
        socket.emit('forum_delete_topic', topicId);
        setSelectedTopic(null);
        setShowDeleteConfirm(false);
        setIsEditingTopic(false);
    };

    const handleStartEditTopic = () => {
        if (!selectedTopic) return;
        setEditTopicTitle(selectedTopic.title);
        setEditTopicCategory(selectedTopic.category);
        setEditTopicContent(selectedTopic.content);
        setIsEditingTopic(true);
    };

    const handleSaveEditTopic = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTopic || !editTopicTitle.trim() || !editTopicContent.trim()) return;

        const updatedTopics = topics.map(t => {
            if (t.id === selectedTopic.id) {
                return {
                    ...t,
                    title: editTopicTitle,
                    category: editTopicCategory,
                    content: editTopicContent
                };
            }
            return t;
        });

        setTopics(updatedTopics);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updatedTopics));
        
        const updatedCurrentTopic = updatedTopics.find(t => t.id === selectedTopic.id);
        if (updatedCurrentTopic) setSelectedTopic(updatedCurrentTopic);
        
        setIsEditingTopic(false);
    };

    const handleDeleteResponse = (respToDelete: ForumResponse) => {
        if (!selectedTopic) return;
        
        const updatedResponses = selectedTopic.responses.filter(r => 
            !(r.author === respToDelete.author && r.createdAt === respToDelete.createdAt && r.text === respToDelete.text)
        );

        const updatedTopics = topics.map(t => {
            if (t.id === selectedTopic.id) {
                return { 
                    ...t, 
                    responses: updatedResponses,
                    replies: Math.max(0, t.replies - 1)
                };
            }
            return t;
        });

        setTopics(updatedTopics);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updatedTopics));
        
        const updatedCurrentTopic = updatedTopics.find(t => t.id === selectedTopic.id);
        if (updatedCurrentTopic) setSelectedTopic(updatedCurrentTopic);
    };

    const handleStartEditResponse = (resp: ForumResponse) => {
        setEditingResponse(resp);
        setEditResponseText(resp.text);
    };

    const handleSaveEditResponse = () => {
        if (!selectedTopic || !editingResponse || !editResponseText.trim()) return;

        const updatedResponses = selectedTopic.responses.map(r => {
            if (r.author === editingResponse.author && r.createdAt === editingResponse.createdAt && r.text === editingResponse.text) {
                return { ...r, text: editResponseText };
            }
            return r;
        });

        const updatedTopics = topics.map(t => {
            if (t.id === selectedTopic.id) {
                return { ...t, responses: updatedResponses };
            }
            return t;
        });

        setTopics(updatedTopics);
        localStorage.setItem('astro_forum_data_v3', JSON.stringify(updatedTopics));
        
        const updatedCurrentTopic = updatedTopics.find(t => t.id === selectedTopic.id);
        if (updatedCurrentTopic) setSelectedTopic(updatedCurrentTopic);
        
        setEditingResponse(null);
        setEditResponseText('');
    };

    const Pagination = () => {
        if (totalPages <= 1) return null;

        let pages = [];
        if (totalPages <= 5) {
            pages = Array.from({length: totalPages}, (_, i) => i + 1);
        } else {
            if (currentPage <= 3) pages = [1, 2, 3, 4, '...', totalPages];
            else if (currentPage >= totalPages - 2) pages = [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
            else pages = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
        }

        return (
            <div className="flex justify-center items-center gap-1.5 sm:gap-2 mt-8 py-4">
                <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-xl bg-[#101726] border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:hover:border-gray-700 transition-colors text-xs font-bold flex items-center gap-1"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Précédent</span>
                </button>
                
                {pages.map((p, idx) => (
                    <button
                        key={idx}
                        onClick={() => typeof p === 'number' && setCurrentPage(p)}
                        disabled={typeof p !== 'number'}
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                            p === currentPage 
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black shadow-md font-black scale-105' 
                            : typeof p === 'number' 
                                ? 'bg-[#101726] text-gray-400 hover:text-white hover:bg-gray-800 border border-gray-700/80'
                                : 'text-gray-600 bg-transparent border-none cursor-default'
                        }`}
                    >
                        {p}
                    </button>
                ))}

                <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-xl bg-[#101726] border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:hover:border-gray-700 transition-colors text-xs font-bold flex items-center gap-1"
                >
                    <span className="hidden sm:inline">Suivant</span>
                    <ChevronRightIcon className="w-4 h-4" />
                </button>
            </div>
        );
    };

    return (
        <section className="py-4 sm:py-8 lg:py-10 bg-gradient-to-b from-[#090D14] via-[#0D121F] to-[#090D14] min-h-screen text-gray-200 overscroll-contain">
            {/* FLOATING ACTION BUTTON (FAB) REDDIT MOBILE */}
            <button
                onClick={handleCreateTopicClick}
                className="lg:hidden fixed bottom-6 right-5 z-40 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-black font-black p-3.5 sm:p-4 rounded-full shadow-2xl shadow-orange-500/40 border border-amber-300 flex items-center gap-2 active:scale-90 transition-transform"
                title="Créer une publication"
            >
                <PlusIcon className="w-6 h-6 stroke-[3]" />
                <span className="text-xs uppercase tracking-wider font-extrabold pr-1 hidden xs:inline">Publier</span>
            </button>

            {/* 1. MODALE CRÉATION DE POST */}
            {showNewTopicForm && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md animate-fade-in">
                    <div className="bg-[#121927] w-full max-w-2xl rounded-2xl border border-gray-700/80 shadow-2xl overflow-hidden flex flex-col max-h-[95vh] animate-scale-up">
                        <div className="p-3.5 sm:p-5 border-b border-gray-700/80 flex justify-between items-center bg-[#0d131f]">
                            <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                                <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-black/70 border border-amber-500/40 flex items-center justify-center p-1 shadow shrink-0 overflow-hidden">
                                    <img src="/logo.png" alt="AstroX" className="w-full h-full object-contain" />
                                </span>
                                <div className="min-w-0 truncate">
                                    <h2 className="text-sm sm:text-base font-black text-white truncate">Créer une publication dans r/AstroX</h2>
                                    <p className="text-[10px] sm:text-[11px] text-gray-400 truncate">Bienveillance & respect des règles</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setShowNewTopicForm(false)} 
                                className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white flex items-center justify-center transition-colors shrink-0 ml-2"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar">
                            <form onSubmit={handleSubmitTopic} className="space-y-3.5 sm:space-y-4">
                                <div>
                                    <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wider mb-1">
                                        Catégorie / Flair <span className="text-amber-400">*</span>
                                    </label>
                                    <select 
                                        value={newTopicCategory} 
                                        onChange={(e) => setNewTopicCategory(e.target.value)} 
                                        className="w-full bg-[#0a0f18] border border-gray-700 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-white focus:border-amber-500 focus:outline-none font-semibold"
                                    >
                                        {creationCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wider mb-1">
                                        Titre du post <span className="text-amber-400">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        placeholder="Un titre clair et captivant..." 
                                        value={newTopicTitle} 
                                        onChange={(e) => setNewTopicTitle(e.target.value)} 
                                        maxLength={180}
                                        className="w-full bg-[#0a0f18] border border-gray-700 rounded-xl px-3.5 py-2.5 sm:py-3 text-xs sm:text-sm text-white focus:border-amber-500 focus:outline-none font-medium placeholder-gray-500" 
                                        required 
                                    />
                                    <span className="text-[10px] text-gray-500 text-right block mt-1">{newTopicTitle.length}/180 caractères</span>
                                </div>

                                <div>
                                    <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wider mb-1">
                                        Corps du message <span className="text-amber-400">*</span>
                                    </label>
                                    <textarea 
                                        placeholder="Détaillez votre situation, ressenti, question de tarot ou témoignage..." 
                                        value={newTopicContent} 
                                        onChange={(e) => setNewTopicContent(e.target.value)} 
                                        className="w-full h-36 sm:h-44 bg-[#0a0f18] border border-gray-700 rounded-xl p-3 sm:p-4 text-xs sm:text-sm text-white resize-none focus:border-amber-500 focus:outline-none placeholder-gray-500 leading-relaxed custom-scrollbar" 
                                        required 
                                    />
                                </div>

                                <div className="flex justify-end gap-2.5 pt-2 border-t border-gray-800">
                                    <button 
                                        type="button" 
                                        onClick={() => setShowNewTopicForm(false)} 
                                        className="px-4 py-2 rounded-xl text-xs font-bold text-gray-300 hover:bg-white/5 transition-colors"
                                    >
                                        Annuler
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={isSubmitting} 
                                        className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-400 hover:to-orange-400 text-black font-black py-2.5 px-6 rounded-xl shadow-lg transition-transform active:scale-95 disabled:opacity-50 text-xs uppercase tracking-wide flex items-center gap-1.5"
                                    >
                                        {isSubmitting ? 'Publication...' : 'Publier le Post'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. VUE DÉTAILLÉE D'UN POST */}
            {selectedTopic ? (
                <div className="container mx-auto px-2.5 sm:px-4 max-w-5xl space-y-4 sm:space-y-6">
                    <div className="flex items-center justify-between gap-2">
                        <button 
                            onClick={handleBack} 
                            className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold text-amber-400 hover:text-amber-300 bg-[#101726] border border-gray-700/80 px-3 sm:px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95"
                        >
                            <span>←</span>
                            <span className="hidden xs:inline">Retour au flux</span>
                            <span>r/AstroX</span>
                        </button>

                        <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-gray-400">
                            <span>{selectedTopic.views || 1} vues</span>
                            <span>•</span>
                            <span><MessageCircleIcon className="w-3.5 h-3.5 inline mr-1 text-purple-300" /> {selectedTopic.responses.length} réponses</span>
                        </div>
                    </div>

                    {showDeleteConfirm && (
                        <div className="bg-red-950/70 border border-red-500/50 p-3.5 sm:p-4 rounded-xl animate-fade-in flex flex-col sm:flex-row justify-between items-center gap-3 shadow-lg">
                            <div className="text-left min-w-0">
                                <h4 className="text-red-200 font-bold text-xs sm:text-sm">Supprimer cette publication ?</h4>
                                <p className="text-gray-300 text-[11px] sm:text-xs mt-0.5">Cette action supprimera le sujet ainsi que toutes les réponses associées.</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button 
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="px-3 py-1.5 text-xs text-gray-300 hover:bg-white/10 border border-gray-600 rounded-lg font-bold transition-all"
                                >
                                    Annuler
                                </button>
                                <button 
                                    onClick={() => handleDeleteTopic(selectedTopic.id)}
                                    className="px-3 py-1.5 text-xs text-white bg-red-600 hover:bg-red-500 rounded-lg font-bold shadow-md transition-all active:scale-95"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="bg-[#101726] border border-gray-700/80 rounded-2xl overflow-hidden shadow-2xl">
                        <div className="p-3.5 sm:p-6 border-b border-gray-800/80 bg-[#0d131f] flex flex-col gap-2.5">
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px] sm:text-xs">
                                <span className="font-bold text-white bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full">
                                    r/AstroX
                                </span>
                                <span className={`font-bold px-2 py-0.5 rounded-full border text-[10px] sm:text-[11px] ${getCategoryFlairStyle(selectedTopic.category)}`}>
                                    {selectedTopic.category}
                                </span>
                                <span className="text-gray-400">• Posté par <strong className="text-gray-200">{selectedTopic.author}</strong></span>
                                <span className="text-gray-500">• {formatRelativeTime(selectedTopic.createdAt, selectedTopic.date)}</span>
                            </div>

                            <h1 className="text-lg sm:text-2xl lg:text-3xl font-black text-white leading-tight break-words overflow-hidden">
                                {selectedTopic.title}
                            </h1>

                            {client && selectedTopic.author === client.name && !isEditingTopic && (
                                <div className="flex gap-2 pt-2 border-t border-gray-800/80">
                                    <button 
                                        onClick={handleStartEditTopic}
                                        className="text-xs font-bold text-gray-300 hover:text-amber-400 bg-gray-800/80 hover:bg-gray-800 border border-gray-700 px-3 py-1.5 rounded-lg transition-all"
                                    >
                                        Modifier
                                    </button>
                                    <button 
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="text-xs font-bold text-gray-300 hover:text-red-400 bg-gray-800/80 hover:bg-gray-800 border border-gray-700 px-3 py-1.5 rounded-lg transition-all"
                                    >
                                        Supprimer
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="p-3.5 sm:p-6 lg:p-8">
                            {isEditingTopic ? (
                                <form onSubmit={handleSaveEditTopic} className="space-y-3.5">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Titre</label>
                                        <input 
                                            type="text" 
                                            value={editTopicTitle} 
                                            onChange={(e) => setEditTopicTitle(e.target.value)} 
                                            className="w-full bg-[#0a0f18] border border-gray-700 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-white focus:border-amber-500 focus:outline-none" 
                                            required 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Catégorie</label>
                                        <select 
                                            value={editTopicCategory} 
                                            onChange={(e) => setEditTopicCategory(e.target.value)} 
                                            className="w-full bg-[#0a0f18] border border-gray-700 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-white focus:border-amber-500 focus:outline-none"
                                        >
                                            {creationCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Message</label>
                                        <textarea 
                                            value={editTopicContent} 
                                            onChange={(e) => setEditTopicContent(e.target.value)} 
                                            className="w-full h-40 bg-[#0a0f18] border border-gray-700 rounded-xl p-3 text-xs sm:text-sm text-white resize-none focus:border-amber-500 focus:outline-none leading-relaxed" 
                                            required 
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button 
                                            type="button" 
                                            onClick={() => setIsEditingTopic(false)} 
                                            className="px-3.5 py-2 rounded-xl text-xs font-bold text-gray-400 hover:text-white"
                                        >
                                            Annuler
                                        </button>
                                        <button 
                                            type="submit" 
                                            className="bg-amber-500 hover:bg-amber-400 text-black font-black px-5 py-2 rounded-xl text-xs uppercase"
                                        >
                                            Enregistrer
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="space-y-4 sm:space-y-6">
                                    <div className="text-gray-200 text-xs sm:text-sm lg:text-base leading-relaxed break-words overflow-hidden whitespace-pre-line bg-[#0c121e]/80 p-4 sm:p-5 rounded-xl border border-gray-800">
                                        {selectedTopic.content}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 border-t border-gray-800 text-xs font-bold text-gray-400">
                                        <div className="flex items-center bg-[#0a0f18] border border-gray-700/80 rounded-full px-2 py-1 gap-1">
                                            <button 
                                                onClick={(e) => handleVote(selectedTopic.id, 'up', e)}
                                                className={`p-1 rounded-full transition-colors ${userVotes[selectedTopic.id] === 'up' ? 'text-orange-500 bg-orange-500/20 font-black' : 'hover:text-orange-400'}`}
                                                title="Upvote"
                                            >
                                                ▲
                                            </button>
                                            <span className={`font-black text-xs px-1 ${userVotes[selectedTopic.id] === 'up' ? 'text-orange-400' : userVotes[selectedTopic.id] === 'down' ? 'text-indigo-400' : 'text-gray-200'}`}>
                                                {getTopicCurrentVotes(selectedTopic)}
                                            </span>
                                            <button 
                                                onClick={(e) => handleVote(selectedTopic.id, 'down', e)}
                                                className={`p-1 rounded-full transition-colors ${userVotes[selectedTopic.id] === 'down' ? 'text-indigo-400 bg-indigo-500/20 font-black' : 'hover:text-indigo-400'}`}
                                                title="Downvote"
                                            >
                                                ▼
                                            </button>
                                        </div>

                                        <button 
                                            onClick={(e) => handleSharePost(selectedTopic, e)}
                                            className="px-3 py-1.5 rounded-full bg-[#0a0f18] hover:bg-gray-800 border border-gray-700/80 hover:text-white transition-all flex items-center gap-1.5"
                                        >
                                            <ExternalLinkIcon className="w-4 h-4 inline" />
                                            <span>{copiedPostId === selectedTopic.id ? 'Lien Copié !' : 'Partager'}</span>
                                        </button>

                                        <button 
                                            onClick={(e) => handleToggleSave(selectedTopic.id, e)}
                                            className={`px-3 py-1.5 rounded-full border transition-all flex items-center gap-1.5 ${savedPosts[selectedTopic.id] ? 'bg-amber-950/60 border-amber-500/40 text-amber-300' : 'bg-[#0a0f18] hover:bg-gray-800 border-gray-700/80 hover:text-white'}`}
                                        >
                                            <StarIcon className={`w-4 h-4 inline ${savedPosts[selectedTopic.id] ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} />
                                            <span>{savedPosts[selectedTopic.id] ? 'Enregistré' : 'Sauvegarder'}</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-800 space-y-4 sm:space-y-6">
                                <h3 className="text-base sm:text-lg font-black text-white flex items-center gap-2">
                                    <MessageSquareIcon className="w-5 h-5 text-amber-400" />
                                    <span>Commentaires ({selectedTopic.responses.length})</span>
                                </h3>

                                <div className="bg-[#0b101a] p-3.5 sm:p-5 rounded-xl border border-gray-700/80 shadow-lg">
                                    {client ? (
                                        <form onSubmit={handleSubmitReply} className="space-y-3">
                                            {replyingToComment && (
                                                <div className="bg-amber-950/70 border border-amber-500/50 p-2.5 px-3.5 rounded-xl flex items-center justify-between text-xs text-amber-300 animate-fade-in shadow-md">
                                                    <span className="flex items-center gap-1.5">
                                                        <span>↩️ En réponse à</span>
                                                        <strong className="text-white font-bold">{replyingToComment.author}</strong>
                                                    </span>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setReplyingToComment(null)}
                                                        className="text-gray-400 hover:text-white font-black px-2 py-0.5 rounded bg-black/40 text-[11px]"
                                                    >
                                                        Annuler
                                                    </button>
                                                </div>
                                            )}
                                            <div className="text-[11px] sm:text-xs text-gray-400 font-bold flex items-center gap-2">
                                                <span>Commenter en tant que <strong className="text-amber-300">{client.name}</strong></span>
                                            </div>
                                            <textarea 
                                                id="forum-reply-input"
                                                value={replyText} 
                                                maxLength={450}
                                                onChange={(e) => setReplyText(e.target.value)} 
                                                placeholder={replyingToComment ? `Votre réponse à ${replyingToComment.author}...` : "Partagez vos ressentis, conseils ou avis..."} 
                                                className="w-full bg-[#070b12] border border-gray-700 rounded-xl p-3 text-xs sm:text-sm text-white focus:border-amber-500 focus:outline-none resize-none h-20 sm:h-24 placeholder-gray-500 leading-relaxed custom-scrollbar" 
                                                required 
                                            />
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-gray-500 font-mono">
                                                    {replyText.length}/450 car.
                                                </span>
                                                <button 
                                                    type="submit" 
                                                    disabled={isSubmitting || !replyText.trim()} 
                                                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black py-2 px-5 sm:px-6 rounded-xl shadow-md transition-transform active:scale-95 disabled:opacity-50 text-xs uppercase tracking-wider flex items-center gap-1.5"
                                                >
                                                    {isSubmitting ? 'Envoi...' : <><SendIcon className="w-3.5 h-3.5" /> Commenter</>}
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className="text-center py-4 space-y-2.5">
                                            <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto text-amber-400">
                                                <LockIcon className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h4 className="text-xs sm:text-sm font-bold text-white">Rejoignez la discussion</h4>
                                                <p className="text-[11px] text-gray-400">Connectez-vous pour commenter et interagir avec la communauté.</p>
                                            </div>
                                            <button 
                                                onClick={() => openAuthModal()} 
                                                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black py-2 px-5 rounded-full shadow text-xs uppercase tracking-wider transition-transform active:scale-95"
                                            >
                                                Connexion / Inscription
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-3 sm:space-y-4">
                                    {sortedResponses.length > 0 ? (
                                        sortedResponses.map((resp, i) => (
                                            <div 
                                                key={i} 
                                                className={`p-3 sm:p-4 rounded-xl border transition-all ${
                                                    resp.isUser || (client && resp.author === client.name) 
                                                        ? 'bg-indigo-950/30 border-indigo-500/40 shadow' 
                                                        : 'bg-[#0c121e] border-gray-800'
                                                }`}
                                            >
                                                {resp.replyToAuthor && (
                                                    <div className="flex items-center gap-1.5 text-[10px] text-amber-400 font-bold mb-1.5 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-md inline-flex">
                                                        <span>↳ En réponse à</span>
                                                        <span className="text-white font-black">{resp.replyToAuthor}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-black shrink-0 ${
                                                            (resp.isUser || (client && resp.author === client.name))
                                                                ? 'bg-gradient-to-tr from-indigo-500 to-purple-500 text-white' 
                                                                : 'bg-gray-700 text-gray-200'
                                                        }`}>
                                                            {resp.author.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                                            <span className="font-bold text-[11px] sm:text-xs text-gray-200">
                                                                {resp.author}
                                                            </span>
                                                            {(resp.isUser || (client && resp.author === client.name)) && (
                                                                <span className="text-[9px] bg-indigo-500/30 text-indigo-300 px-1.5 py-0.5 rounded-full font-bold">
                                                                    Moi
                                                                </span>
                                                            )}
                                                            <span className="text-[10px] text-gray-500">
                                                                • {formatRelativeTime(resp.createdAt, resp.date)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <button
                                                            onClick={() => {
                                                                if (!client) {
                                                                    openAuthModal();
                                                                    return;
                                                                }
                                                                setReplyingToComment({ id: resp.id || `resp_${i}`, author: resp.author });
                                                                const el = document.getElementById('forum-reply-input');
                                                                if (el) {
                                                                    el.focus();
                                                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                }
                                                            }}
                                                            className="text-gray-400 hover:text-amber-300 text-[11px] font-bold transition-colors flex items-center gap-1 cursor-pointer bg-black/40 px-2 py-0.5 rounded-lg border border-gray-700 hover:border-amber-400"
                                                            title={`Répondre à ${resp.author}`}
                                                        >
                                                            <span>↩️</span>
                                                            <span>Répondre</span>
                                                        </button>

                                                        {(resp.isUser || (client && resp.author === client.name)) && (
                                                            <>
                                                                <button 
                                                                    onClick={() => handleStartEditResponse(resp)}
                                                                    className="text-gray-400 hover:text-amber-400 text-[10px] font-bold transition-colors"
                                                                >
                                                                    Modifier
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleDeleteResponse(resp)}
                                                                    className="text-gray-400 hover:text-red-400 text-[10px] font-bold transition-colors"
                                                                >
                                                                    Supprimer
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {editingResponse && editingResponse.author === resp.author && editingResponse.createdAt === resp.createdAt && editingResponse.text === resp.text ? (
                                                    <div className="mt-2 space-y-2">
                                                        <textarea 
                                                            value={editResponseText} 
                                                            onChange={(e) => setEditResponseText(e.target.value)} 
                                                            className="w-full bg-[#070b12] border border-gray-700 rounded-xl p-2.5 text-xs text-white focus:border-amber-500 focus:outline-none resize-none h-20"
                                                        />
                                                        <div className="flex justify-end gap-1.5">
                                                            <button 
                                                                type="button" 
                                                                onClick={() => setEditingResponse(null)} 
                                                                className="px-2.5 py-1 rounded-lg text-[11px] text-gray-400 hover:text-white"
                                                            >
                                                                Annuler
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                onClick={handleSaveEditResponse} 
                                                                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-[11px] text-black font-black"
                                                            >
                                                                Enregistrer
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-300 text-xs sm:text-sm leading-relaxed pl-7 sm:pl-9 break-words overflow-hidden">
                                                        {resp.text}
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-500 text-xs italic p-4 sm:p-6 bg-[#0c121e] rounded-xl text-center border border-gray-800">
                                            Aucun commentaire pour l'instant. Soyez le premier à réagir !
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* 3. FLUX GÉNÉRAL REDDIT (PAGE D'ACCUEIL DU FORUM) */
                <div className="container mx-auto px-2.5 sm:px-4 max-w-7xl">
                    <div className="mb-4 sm:mb-6 rounded-2xl overflow-hidden border border-gray-700/80 bg-[#101726] shadow-xl">
                        <div className="h-20 sm:h-28 lg:h-36 bg-gradient-to-r from-indigo-950 via-purple-900 to-amber-950 relative">
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-black/40"></div>
                        </div>

                        <div className="px-3.5 sm:px-6 pb-3.5 pt-0 relative flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 -mt-8 sm:-mt-12">
                            <div className="flex items-end gap-2.5 sm:gap-4 min-w-0">
                                <div className="w-16 h-16 sm:w-22 sm:h-22 rounded-2xl bg-black/90 border-3 sm:border-4 border-[#101726] shadow-2xl flex items-center justify-center p-2 shrink-0 overflow-hidden bg-gradient-to-tr from-amber-500/20 via-purple-950/40 to-black/90 backdrop-blur-md">
                                    <img src="/logo.png" alt="AstroX Logo" className="w-full h-full object-contain drop-shadow" />
                                </div>
                                <div className="mb-0.5 min-w-0 truncate">
                                    <div className="flex items-center gap-1.5">
                                        <h1 className="text-lg sm:text-2xl lg:text-3xl font-black text-white flex items-center gap-1 truncate">
                                            r/AstroX
                                        </h1>
                                        <span className="text-[9px] sm:text-[10px] bg-amber-500 text-black px-1.5 sm:px-2 py-0.5 rounded-full font-black uppercase tracking-wider shrink-0">
                                            Officiel
                                        </span>
                                    </div>
                                    <p className="text-[11px] sm:text-xs text-gray-400 truncate mt-0.5">
                                        La communauté ésotérique & voyance n°1 de France
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0 pt-1 sm:pt-0">
                                <button
                                    onClick={() => setIsJoinedCommunity(!isJoinedCommunity)}
                                    className={`flex-1 sm:flex-none px-3.5 py-1.5 sm:py-2 rounded-full text-xs font-extrabold transition-all active:scale-95 border ${
                                        isJoinedCommunity 
                                            ? 'bg-gray-800 text-gray-300 border-gray-600 hover:border-red-500/50 hover:text-red-300' 
                                            : 'bg-gradient-to-r from-amber-500 to-orange-500 text-black border-amber-400 shadow'
                                    }`}
                                >
                                    {isJoinedCommunity ? 'Rejoint' : 'Rejoindre +'}
                                </button>
                                <button
                                    onClick={handleCreateTopicClick}
                                    className="flex-1 sm:flex-none px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-extrabold shadow transition-all active:scale-95 flex items-center justify-center gap-1.5"
                                >
                                    <PlusIcon className="w-3.5 h-3.5" />
                                    <span>Publier</span>
                                </button>
                                <button
                                    onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                                    className="lg:hidden p-2 rounded-full bg-[#0a0f18] border border-gray-700 text-gray-300 text-xs font-bold"
                                    title="Règles et infos"
                                >
                                    ℹ️
                                </button>
                            </div>
                        </div>
                    </div>

                    {showMobileSidebar && (
                        <div className="lg:hidden mb-4 p-4 rounded-2xl bg-[#101726] border border-amber-500/40 shadow-2xl animate-scale-up space-y-3">
                            <div className="flex items-center justify-between border-b border-gray-800 pb-2">
                                <h3 className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="text-[10px] text-amber-300 font-bold uppercase tracking-wider">Épinglé</span>
                                    <span>À propos & Règles r/AstroX</span>
                                </h3>
                                <button onClick={() => setShowMobileSidebar(false)} className="text-gray-400 text-xs font-bold">Fermer</button>
                            </div>
                            <p className="text-xs text-gray-300">
                                <strong>15.8k Membres</strong> • <strong className="text-emerald-400">{onlineCount} En ligne</strong>. Espace d'entraide et d'échanges bienveillants.
                            </p>
                            <div className="text-[11px] text-gray-400 space-y-1">
                                <div>• 1. Respect & Bienveillance</div>
                                <div>• 2. Pas de publicité ni spam</div>
                                <div>• 3. Partages authentiques</div>
                            </div>
                        </div>
                    )}

                    <div 
                        onClick={handleCreateTopicClick}
                        className="mb-3.5 bg-[#101726] border border-gray-700/80 hover:border-gray-600 p-2.5 sm:p-3.5 rounded-2xl shadow flex items-center gap-2.5 cursor-pointer transition-all group"
                    >
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-tr from-amber-500 to-orange-500 flex items-center justify-center text-xs font-black text-black shrink-0 shadow">
                            {client ? client.name.charAt(0).toUpperCase() : <UserIcon className="w-4 h-4 inline" />}
                        </div>
                        <input 
                            type="text" 
                            placeholder="Créer une publication dans r/AstroX..." 
                            readOnly 
                            className="flex-1 bg-[#0a0f18] hover:bg-[#0d131f] border border-gray-700/80 rounded-xl px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-300 cursor-pointer focus:outline-none transition-colors"
                        />
                        <div className="hidden sm:flex items-center gap-2 text-gray-400 text-xs font-bold shrink-0">
                            <span className="p-1.5 hover:bg-white/5 rounded-lg">Texte</span>
                            <span className="p-1.5 hover:bg-white/5 rounded-lg">🃏 Tirage</span>
                            <span className="p-1.5 hover:bg-white/5 rounded-lg">Astres</span>
                        </div>
                    </div>

                    <div className="mb-4 sm:mb-6 bg-[#101726] border border-gray-700/80 p-2.5 sm:p-3 rounded-2xl shadow flex flex-col lg:flex-row items-center justify-between gap-2.5 sm:gap-3">
                        <div className="flex items-center gap-1.5 w-full lg:w-auto overflow-x-auto custom-scrollbar pb-1 lg:pb-0">
                            <button
                                onClick={() => setSortBy('hot')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold flex items-center gap-1 transition-all whitespace-nowrap ${
                                    sortBy === 'hot' 
                                        ? 'bg-orange-600/30 text-orange-400 border border-orange-500/50 shadow-sm' 
                                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                            >
                                <TrendingUpIcon className="w-3.5 h-3.5 text-amber-400 inline" />
                                <span>Populaire</span>
                            </button>
                            <button
                                onClick={() => setSortBy('new')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold flex items-center gap-1 transition-all whitespace-nowrap ${
                                    sortBy === 'new' 
                                        ? 'bg-blue-600/30 text-blue-400 border border-blue-500/50 shadow-sm' 
                                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                            >
                                <SparklesIcon className="w-3.5 h-3.5 text-purple-400 inline" />
                                <span>Nouveau</span>
                            </button>
                            <button
                                onClick={() => setSortBy('top')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold flex items-center gap-1 transition-all whitespace-nowrap ${
                                    sortBy === 'top' 
                                        ? 'bg-amber-600/30 text-amber-400 border border-amber-500/50 shadow-sm' 
                                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                            >
                                <AwardIcon className="w-3.5 h-3.5 text-amber-400 inline" />
                                <span>Meilleurs</span>
                            </button>
                        </div>

                        <div className="flex items-center gap-1.5 w-full lg:w-auto overflow-x-auto custom-scrollbar pb-1 lg:pb-0">
                            {categories.map(cat => (
                                <button 
                                    key={cat} 
                                    onClick={() => setSelectedCategory(cat)} 
                                    className={`px-2.5 py-1 rounded-lg text-[11px] sm:text-xs font-bold whitespace-nowrap transition-all border ${
                                        selectedCategory === cat 
                                            ? 'bg-amber-500 text-black border-amber-400 shadow font-black' 
                                            : 'bg-gray-900/60 text-gray-400 hover:text-white border-gray-700/60 hover:bg-gray-800'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        <div className="relative w-full lg:w-56 shrink-0">
                            <input 
                                type="text" 
                                placeholder="Rechercher..." 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                                className="w-full bg-[#0a0f18] border border-gray-700 rounded-xl pl-3.5 pr-8 py-1.5 text-xs text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none" 
                            />
                            {searchTerm ? (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1.5 text-gray-400 hover:text-white text-xs"><XIcon className="w-3.5 h-3.5 inline" /></button>
                            ) : (
                                <span className="absolute right-2.5 top-1.5 text-gray-500 text-xs"><SearchIcon className="w-3.5 h-3.5" /></span>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                        <div className="lg:col-span-8 space-y-3 sm:space-y-3.5 min-w-0">
                            {currentTopics.map((topic) => {
                                const currentScore = getTopicCurrentVotes(topic);
                                const userVote = userVotes[topic.id];
                                const isSaved = savedPosts[topic.id];

                                return (
                                    <div 
                                        key={topic.id} 
                                        onClick={() => handleTopicClick(topic)} 
                                        className="group bg-[#101726] hover:bg-[#131b2e] border border-gray-700/70 hover:border-amber-500/40 rounded-2xl overflow-hidden shadow transition-all duration-200 cursor-pointer flex flex-col sm:flex-row min-w-0"
                                    >
                                        <div 
                                            onClick={(e) => e.stopPropagation()}
                                            className="hidden sm:flex flex-col items-center justify-start p-3 bg-[#0a0f18]/60 border-r border-gray-800/80 w-12 shrink-0 gap-1 select-none"
                                        >
                                            <button 
                                                onClick={(e) => handleVote(topic.id, 'up', e)}
                                                className={`p-1 rounded-md transition-all ${userVote === 'up' ? 'text-orange-500 bg-orange-500/20 font-black scale-110' : 'text-gray-400 hover:text-orange-400 hover:bg-white/5'}`}
                                                title="Upvote"
                                            >
                                                ▲
                                            </button>
                                            <span className={`text-xs font-black ${userVote === 'up' ? 'text-orange-400' : userVote === 'down' ? 'text-indigo-400' : 'text-gray-200'}`}>
                                                {currentScore}
                                            </span>
                                            <button 
                                                onClick={(e) => handleVote(topic.id, 'down', e)}
                                                className={`p-1 rounded-md transition-all ${userVote === 'down' ? 'text-indigo-400 bg-indigo-500/20 font-black scale-110' : 'text-gray-400 hover:text-indigo-400 hover:bg-white/5'}`}
                                                title="Downvote"
                                            >
                                                ▼
                                            </button>
                                        </div>

                                        <div className="p-3 sm:p-4.5 flex-1 min-w-0 flex flex-col justify-between gap-2">
                                            <div className="flex flex-wrap items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-400">
                                                <span className="font-bold text-amber-400">r/AstroX</span>
                                                <span>•</span>
                                                <span className={`font-bold px-2 py-0.5 rounded-full border text-[9px] sm:text-[10px] ${getCategoryFlairStyle(topic.category)}`}>
                                                    {topic.category}
                                                </span>
                                                <span className="truncate max-w-[120px] sm:max-w-none">• {topic.author}</span>
                                                <span>•</span>
                                                <span className="text-gray-500">{formatRelativeTime(topic.createdAt, topic.date)}</span>
                                            </div>

                                            <h3 className="text-xs sm:text-sm lg:text-base font-bold text-white group-hover:text-amber-300 transition-colors leading-snug break-words overflow-hidden">
                                                {topic.title}
                                            </h3>

                                            <p className="text-[11px] sm:text-xs text-gray-400 line-clamp-2 leading-relaxed break-words overflow-hidden">
                                                {topic.content}
                                            </p>

                                            <div className="flex items-center justify-between pt-2 border-t border-gray-800/80 text-[10px] sm:text-[11px] font-bold text-gray-400">
                                                <div className="flex items-center gap-1.5 sm:gap-2">
                                                    <div 
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="sm:hidden flex items-center bg-[#0a0f18] border border-gray-700/80 rounded-full px-2 py-0.5 gap-1.5"
                                                    >
                                                        <button 
                                                            onClick={(e) => handleVote(topic.id, 'up', e)}
                                                            className={`p-0.5 ${userVote === 'up' ? 'text-orange-500 font-black' : 'hover:text-orange-400'}`}
                                                        >
                                                            ▲
                                                        </button>
                                                        <span className="font-black text-[11px]">{currentScore}</span>
                                                        <button 
                                                            onClick={(e) => handleVote(topic.id, 'down', e)}
                                                            className={`p-0.5 ${userVote === 'down' ? 'text-indigo-400 font-black' : 'hover:text-indigo-400'}`}
                                                        >
                                                            ▼
                                                        </button>
                                                    </div>

                                                    <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-[#0a0f18] border border-gray-700/60 flex items-center gap-1 text-gray-300">
                                                        <MessageCircleIcon className="w-3.5 h-3.5 inline" />
                                                        <span>{topic.replies || topic.responses.length || 0}</span>
                                                        <span className="hidden sm:inline">Rép.</span>
                                                    </span>

                                                    <button 
                                                        onClick={(e) => handleSharePost(topic, e)}
                                                        className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-[#0a0f18] hover:bg-gray-800 border border-gray-700/60 hover:text-white flex items-center gap-1 transition-all"
                                                    >
                                                        <ExternalLinkIcon className="w-4 h-4 inline" />
                                                        <span className="hidden sm:inline">{copiedPostId === topic.id ? 'Copié !' : 'Partager'}</span>
                                                    </button>

                                                    <button 
                                                        onClick={(e) => handleToggleSave(topic.id, e)}
                                                        className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border transition-all flex items-center gap-1 ${isSaved ? 'bg-amber-950/60 border-amber-500/40 text-amber-300' : 'bg-[#0a0f18] hover:bg-gray-800 border-gray-700/60 hover:text-white'}`}
                                                    >
                                                        <StarIcon className={`w-3.5 h-3.5 inline ${isSaved ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} />
                                                        <span className="hidden sm:inline">{isSaved ? 'Enregistré' : 'Sauvegarder'}</span>
                                                    </button>
                                                </div>

                                                <span className="text-[10px] text-gray-500 shrink-0">
                                                    {topic.views || 10} vues
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {currentTopics.length === 0 && (
                                <div className="text-center py-12 sm:py-16 bg-[#101726] rounded-2xl border border-gray-800 p-6 sm:p-8 space-y-3 shadow-xl">
                                    <SparklesIcon className="w-8 h-8 text-purple-400 mx-auto animate-pulse" />
                                    <h3 className="text-sm sm:text-base font-bold text-white">Le forum est prêt pour de nouvelles discussions</h3>
                                    <p className="text-xs text-gray-400 max-w-md mx-auto leading-relaxed">
                                        Soyez le premier à poser votre question ou partagez votre expérience spirituelle avec la communauté !
                                    </p>
                                    <button 
                                        onClick={handleCreateTopicClick} 
                                        className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-400 hover:to-orange-400 text-black font-black py-2.5 px-6 rounded-xl shadow-lg transition-transform active:scale-95 text-xs uppercase tracking-wide inline-flex items-center gap-2 mt-2"
                                    >
                                        
                                        <span>Créer un Post</span>
                                    </button>
                                </div>
                            )}

                            <Pagination />
                        </div>

                        <div className="lg:col-span-4 space-y-4 sm:space-y-5">
                            <div className="bg-[#101726] border border-gray-700/80 rounded-2xl p-4 sm:p-5 shadow-xl space-y-3.5">
                                <div className="flex items-center gap-3 border-b border-gray-800 pb-3">
                                    <span className="w-9 h-9 rounded-xl bg-black/80 border border-amber-500/40 flex items-center justify-center p-1.5 shadow shrink-0 overflow-hidden">
                                        <img src="/logo.png" alt="AstroX" className="w-full h-full object-contain" />
                                    </span>
                                    <div>
                                        <h3 className="text-xs sm:text-sm font-black text-white">À propos de r/AstroX</h3>
                                        <span className="text-[10px] sm:text-[11px] text-gray-400">Créé en 2024 • Communauté publique</span>
                                    </div>
                                </div>

                                <p className="text-xs text-gray-300 leading-relaxed">
                                    Espace d'échange dédié à la voyance, l'astrologie, la cartomancie et la spiritualité. Posez vos questions et échangez avec des passionnés et médiums certifiés.
                                </p>

                                <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-gray-800">
                                    <div className="bg-[#0a0f18] p-2.5 rounded-xl border border-gray-800 text-center">
                                        <div className="text-sm sm:text-base font-black text-white">15.8k</div>
                                        <div className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase">Membres</div>
                                    </div>
                                    <div className="bg-[#0a0f18] p-2.5 rounded-xl border border-gray-800 text-center">
                                        <div className="text-sm sm:text-base font-black text-emerald-400 flex items-center justify-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                            {onlineCount}
                                        </div>
                                        <div className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase">En ligne</div>
                                    </div>
                                </div>

                                <button
                                    onClick={handleCreateTopicClick}
                                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black text-xs uppercase tracking-wider transition-all shadow active:scale-95 flex items-center justify-center gap-1.5"
                                >
                                    <PlusIcon className="w-3.5 h-3.5" />
                                    <span>Créer une publication</span>
                                </button>
                            </div>

                            <div className="bg-[#101726] border border-gray-700/80 rounded-2xl p-4 sm:p-5 shadow-xl space-y-2.5 sm:space-y-3">
                                <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2 border-b border-gray-800 pb-2.5">
                                    
                                    <span>Règles de r/AstroX</span>
                                </h3>

                                <ul className="space-y-2 text-xs text-gray-300">
                                    <li className="flex items-start gap-1.5">
                                        <span className="text-amber-400 font-bold">1.</span>
                                        <span><strong>Respect & Bienveillance</strong> : Aucun propos haineux toléré.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5">
                                        <span className="text-amber-400 font-bold">2.</span>
                                        <span><strong>Pas de pub</strong> : Spam et autopromotion interdits.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5">
                                        <span className="text-amber-400 font-bold">3.</span>
                                        <span><strong>Authenticité</strong> : Partages et ressentis sincères.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5">
                                        <span className="text-amber-400 font-bold">4.</span>
                                        <span><strong>Confidentialité</strong> : Respect de l'anonymat.</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-gradient-to-br from-indigo-950/80 via-purple-950/60 to-gray-900 border border-indigo-500/40 rounded-2xl p-4 sm:p-5 shadow-xl space-y-3">
                                <div className="flex items-center gap-2 text-indigo-300 text-xs font-black uppercase tracking-wider">
                                    <SparklesIcon className="w-3.5 h-3.5 text-purple-400 inline" />
                                    <span>Besoin d'une réponse urgente ?</span>
                                </div>

                                <p className="text-xs text-gray-300 leading-relaxed">
                                    Nos experts médiums et tarologues sont disponibles <strong>24h/24 en direct</strong> par téléphone.
                                </p>

                                <a 
                                    href="tel:0985405274" 
                                    className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-500 hover:to-green-400 text-white font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 active:scale-95"
                                >
                                    <PhoneIcon className="w-4 h-4" />
                                    <span>Appeler un Voyant (0,80€/min)</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Forum;
